import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Input, message } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  apiCheckWarranty,
  apiLandingConfig,
  apiScanWarranty,
  apiSelectTelecom,
  apiSetTopupPhone,
  type LandingConfigData,
  type WarrantyData,
} from "@/api/warranty";
import { TEMPLATE_TYPES } from "@/constants";
import { buildTemplateFourTheme } from "@/utils/template4Theme";
import TemplateFour from "@/components/templates/verifies/TemplateFour";
import WarrantyFooter from "@/components/templates/verifies/WarrantyFooter";
import logoCanon from "@/assets/images/logo-canon.svg";
import "@/components/templates/verifies/baohanh-barcode.css";

/**
 * Route `/kichhoat` — clone chính xác giao diện app cũ baohanh-barcode.
 *
 * - Không có `series`/`code` trên query -> form nhập tay (Mã cào + serial) = pages/index.vue.
 * - Có `series`/`code` (link/QR đã in)   -> gọi /check và render TemplateFour = pages/kichhoat.vue.
 */
const KichHoatView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [entryForm] = Form.useForm();

  const series = (searchParams.get("series") || "").trim();
  const code = (searchParams.get("code") || "").trim();
  const hasParams = !!(series && code);

  // Chế độ xem trước (preview=1): chỉ hiển thị, KHÔNG cho nhập/kích hoạt bảo hành.
  const previewParam = (searchParams.get("preview") || "").trim();
  const isPreview = previewParam === "1" || previewParam.toLowerCase() === "true";

  const [loading, setLoading] = useState<boolean>(hasParams);
  const [data, setData] = useState<WarrantyData | null>(null);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [activating, setActivating] = useState<boolean>(false);
  const [selectingTelecom, setSelectingTelecom] = useState<boolean>(false);
  // Vừa nhận thưởng TRONG phiên này (kích hoạt / chọn nhà mạng / nhập SĐT) -> hiện
  // khối trả thưởng (voucher thẻ cào, hoặc chúc mừng nạp tự động). Khi mở lại trang
  // (lần 2, qua /check) cờ này = false -> thông tin xuống list thông tin serial.
  // KHÔNG bó theo status === "issued": nạp tự động là bất đồng bộ nên vừa kích hoạt
  // xong reward mới ở "processing", vẫn phải chúc mừng ngay.
  const [justRewarded, setJustRewarded] = useState<boolean>(false);

  // Cấu hình hiển thị cho chế độ form nhập tay: chưa có serial nên không gọi được
  // /check, phải lấy theme + nội dung tuỳ chỉnh qua endpoint riêng.
  const [landingConfig, setLandingConfig] = useState<LandingConfigData | null>(null);

  useEffect(() => {
    if (hasParams) return;

    let mounted = true;
    (async () => {
      try {
        const result = await apiLandingConfig();
        if (mounted) setLandingConfig(result);
      } catch {
        // Không có cấu hình thì dùng mặc định — không chặn người dùng nhập mã.
      }
    })();

    return () => {
      mounted = false;
    };
  }, [hasParams]);

  const theme = useMemo(() => buildTemplateFourTheme(landingConfig?.template?.data), [landingConfig]);
  const activateNote = landingConfig?.config?.landing_activate_note_content;

  // Gọi /check khi có đủ series + code
  useEffect(() => {
    if (!hasParams) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const result = await apiCheckWarranty({ series, code });
        if (!mounted) return;
        if (!result || result.status === "not_found") {
          setNotFound(true);
        } else if (result.template?.code && result.template.code !== TEMPLATE_TYPES.TEMPLATE_4) {
          // Chuyển đổi template linh hoạt: scan-setting chọn template khác
          // (vd mrw_template_1) thì trả về luồng xác thực chuẩn — URL /kichhoat
          // đã in trên tem vẫn tôn trọng cấu hình admin. Không có vòng lặp:
          // HomeView chỉ redirect sang đây khi template ĐÚNG là mrw_template_4,
          // và cả 2 endpoint đọc template từ cùng một scan-setting.
          const verifyParams = new URLSearchParams({ serial: series });
          if (isPreview) verifyParams.set("preview", "1");
          navigate(`/xac-thuc?${verifyParams.toString()}`, { replace: true });
          return;
        } else {
          setData(result);
        }
      } catch {
        if (mounted) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [series, code, hasParams, isPreview, navigate]);

  // Kích hoạt bảo hành (POST /scan)
  const handleActivate = async (values: { name: string; phone: string }) => {
    // Chế độ xem trước: chặn kích hoạt (không gọi API).
    if (isPreview) {
      message.warning("Chế độ xem trước — không thể kích hoạt bảo hành.");
      return;
    }
    setActivating(true);
    try {
      const result = await apiScanWarranty({
        series,
        code,
        name: values.name.trim(),
        phone: values.phone.trim(),
      });
      if (!result || result.status === "not_found") {
        setNotFound(true);
      } else {
        setData(result);
        // Có reward trong response của hành động kích hoạt = khách vừa nhận thưởng
        // trong phiên này (thẻ cào đã phát / chờ chọn nhà mạng, hoặc topup đang nạp).
        if (result.scratch_card_reward) setJustRewarded(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setActivating(false);
    }
  };

  // C14 — chọn nhà mạng nhận thẻ cào (POST /scan-select-telecom)
  const handleSelectTelecom = async (network: number) => {
    if (isPreview) {
      message.warning("Chế độ xem trước — không thể nhận thẻ.");
      return;
    }
    setSelectingTelecom(true);
    try {
      const result = await apiSelectTelecom({ series, code, network });
      if (result && result.status !== "not_found") {
        setData(result);
        // Vừa chọn nhà mạng & nhận thẻ -> hiện voucher ngay ở khối trả thẻ.
        if (result.scratch_card_reward) setJustRewarded(true);
      }
      if (result?.scratch_card_reward?.status !== "issued" && result?.scratch_card_reward?.error) {
        message.error(result.scratch_card_reward.error);
      }
    } catch {
      message.error("Không thể nhận thẻ lúc này. Vui lòng thử lại.");
    } finally {
      setSelectingTelecom(false);
    }
  };

  // D6 — nhập SĐT để nhận nạp tự động (POST /scan-set-topup-phone)
  const handleSetTopupPhone = async (phone: string) => {
    if (isPreview) {
      message.warning("Chế độ xem trước — không thể nhận nạp.");
      return;
    }
    setSelectingTelecom(true);
    try {
      const result = await apiSetTopupPhone({ series, code, phone });
      if (result && result.status !== "not_found") {
        setData(result);
        if (result.scratch_card_reward) setJustRewarded(true);
      }
      if (
        result?.scratch_card_reward?.status !== "issued" &&
        result?.scratch_card_reward?.status !== "processing" &&
        result?.scratch_card_reward?.error
      ) {
        message.error(result.scratch_card_reward.error);
      }
    } catch {
      message.error("Không thể nạp lúc này. Vui lòng kiểm tra số điện thoại và thử lại.");
    } finally {
      setSelectingTelecom(false);
    }
  };

  // Submit form nhập tay -> điều hướng sang deep-link /kichhoat?series=&code=
  const handleEntrySubmit = (values: { order: string; series: string }) => {
    const params = new URLSearchParams({
      series: values.series.trim(),
      code: values.order.trim(),
    });
    if (isPreview) params.set("preview", "1");
    navigate(`/kichhoat?${params.toString()}`);
  };

  const onEntryFailed = () => {
    message.error("Vui lòng kiểm tra lại thông tin");
  };

  if (hasParams) {
    return (
      <TemplateFour
        loading={loading}
        data={data}
        notFound={notFound}
        activating={activating}
        preview={isPreview}
        onActivate={handleActivate}
        selectingTelecom={selectingTelecom}
        onSelectTelecom={handleSelectTelecom}
        onSetTopupPhone={handleSetTopupPhone}
        justRewarded={justRewarded}
      />
    );
  }

  // --- Form nhập tay (Mã cào + serial) — clone pages/index.vue ---
  return (
    <div className="baohanh-barcode" style={theme.vars}>
      <div className="mx-auto max-w-7xl py-12 sm:px-6 lg:px-8 h-full">
        <div className="mx-auto max-w-4xl">
          <Card>
            <div className="px-6 text-left logo-source">
              <img src={theme.get("logo") || logoCanon} alt="Logo" className="image-logo-source" />
            </div>

            <div className="py-3 sm:px-6">
              <h3 className="gp-heading text-center pt-5 pb-2 rounded-lg text-xl font-bold">
                {theme.get("heading_text")}
              </h3>
            </div>

            <div className="px-4 py-3 sm:px-6">
              {/* Prefill serial khi được HomeView chuyển sang chỉ với series (BE ẩn mã cào) */}
              <Form
                form={entryForm}
                layout="vertical"
                initialValues={{ series }}
                onFinish={handleEntrySubmit}
                onFinishFailed={onEntryFailed}
              >
                <Form.Item
                  label="Mã cào"
                  name="order"
                  rules={[
                    { required: true, whitespace: true, message: "Vui lòng không được để trống" },
                    { min: 8, max: 8, message: "Độ dài giới hạn 8 ký tự" },
                  ]}
                >
                  <Input size="large" placeholder="Nhập mã cào..." />
                </Form.Item>
                <Form.Item
                  label="Mã serial (barcode)"
                  name="series"
                  rules={[
                    { required: true, whitespace: true, message: "Vui lòng không được để trống" },
                    { min: 11, max: 11, message: "Độ dài giới hạn 11 ký tự" },
                    { pattern: /^GPC/, message: "Mã serial không đúng định dạng" },
                  ]}
                >
                  <Input size="large" placeholder="Nhập serial..." />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    danger={!theme.hasCustomActivateButton}
                    htmlType="submit"
                    className={`w-full mt-3${theme.hasCustomActivateButton ? " gp-activate-btn" : ""}`}
                    size="large"
                  >
                    {theme.get("manual_activate_button_label")}
                  </Button>
                </Form.Item>
              </Form>

              {/* Nội dung tuỳ chỉnh admin soạn — ngay dưới nút kích hoạt.
                  HTML đã được backend làm sạch bằng HTMLPurifier ở cả đường ghi lẫn đường đọc. */}
              {activateNote && (
                <div
                  className="gp-note gp-rich-text mt-3 text-sm"
                  dangerouslySetInnerHTML={{ __html: activateNote }}
                />
              )}
            </div>

            <div className="border-t border-gray-200 mb-4" style={{ minHeight: "50px" }}>
              <div className="gp-muted text-center px-2">
                {theme.get("manual_thankyou_text")}{" "}
                <a className="gp-link" href={theme.get("manual_brand_link_url")}>
                  {theme.get("manual_brand_link_label")}
                </a>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-3">
          <WarrantyFooter footerText={theme.get("footer_text")} hotline={theme.get("hotline")} />
        </div>
      </div>
    </div>
  );
};

export default KichHoatView;
